/**
 * Copy del catálogo de planes **según desde dónde se abrió** — ADR-2026-07-30-copy-catalogo-segun-contexto-abrio.
 *
 * El catálogo ya no se abre sólo desde `/subscription`: lo abre el final del alta
 * (después de descargar una foto de ejemplo) y lo abre el intento de generar sin
 * créditos. En cada caso el usuario llega con una cabeza distinta, y un único
 * mensaje genérico desaprovecha el momento.
 *
 * Dónde se ve cada uno:
 * - **Mobile:** reemplaza el título y la bajada del carrusel y se **mantiene fijo
 *   a lo largo de las 4 imágenes** (los pasos siguen avanzando). Así el argumento
 *   no se pierde mientras el carrusel rota.
 * - **Desktop:** va en el panel derecho, arriba del switch Mensual/Anual. El
 *   carrusel de la izquierda conserva su propio copy por imagen.
 *
 * Sin contexto (`/subscription` a secas, `/pricing-dev`) no se pinta nada y el
 * catálogo queda como estaba.
 */

import { palabraDeModeloActual } from '../../lib/nombreDeModelo';

export interface PricingContextCopy {
  title: string;
  description: string;
}

/**
 * Final del alta: el usuario ya vio sus primeras fotos generadas y se descargó
 * una. El mensaje es de ambición — mostrarle que lo que probó es una fracción de
 * lo que hay adentro.
 */
export const PRICING_COPY_ONBOARDING: PricingContextCopy = {
  title: 'Desbloqueá todo el potencial de Estudio Atlas',
  description:
    `Fotos de catálogo, videos para redes, anuncios y ${palabraDeModeloActual().plural} virtuales. Elegí tu plan y producí en minutos todo lo que tu marca necesita.`,
};

/**
 * Se quedó sin créditos al intentar generar. El mensaje reconoce el bloqueo
 * (nombrarlo evita que se sienta un cartel genérico) y empuja a destrabarlo ya,
 * contra la alternativa de esperar la renovación del ciclo.
 */
export const PRICING_COPY_SIN_CREDITOS: PricingContextCopy = {
  title: 'Te quedaste sin créditos',
  description:
    'No frenes justo ahora. Mejorá tu plan y seguí generando fotos, videos y anuncios al instante, sin esperar a fin de mes.',
};

/**
 * Se quedó sin créditos **en la prueba gratis** — ADR-2026-08-03-copy-prueba-gratis-suscribirse.
 *
 * Mismo momento que `PRICING_COPY_SIN_CREDITOS`, pero a este usuario **no se le puede
 * hablar de mejorar un plan**: no tiene ninguno. Tampoco de esperar a fin de mes: la
 * prueba gratis no renueva, así que esperar no le devuelve nada. El mensaje es
 * suscribirse, y que eso destraba la generación ahora.
 */
export const PRICING_COPY_SIN_CREDITOS_PRUEBA: PricingContextCopy = {
  title: 'Te quedaste sin créditos',
  description:
    'Terminaste tus créditos de prueba. Suscribite a un plan y seguí generando fotos, videos y anuncios al instante.',
};

/**
 * Le quedan créditos, pero pocos (por debajo del umbral de `creditsStatus`). Llega
 * desde el CTA de la barra superior, que ya le dijo cuántos le quedan — así que el
 * mensaje no repite el número: se apoya en que todavía puede elegir cuándo, que es
 * la diferencia con `SIN_CREDITOS`.
 */
export const PRICING_COPY_POCOS_CREDITOS: PricingContextCopy = {
  title: 'Te quedan pocos créditos',
  description:
    'Ampliá tu plan antes de quedarte a mitad de camino y seguí generando fotos, videos y anuncios sin interrupciones.',
};

/**
 * Mejora deliberada: tocó "Mejorar plan" teniendo saldo de sobra. Nadie le está
 * bloqueando nada, así que el copy no puede hablar de créditos que se acaban —sería
 * falso—: vende lo que se abre al subir, no lo que se cierra si no sube.
 */
export const PRICING_COPY_MEJORAR_PLAN: PricingContextCopy = {
  title: 'Elegí tu plan',
  description:
    `Más créditos por mes y todo Atlas disponible: fotos de catálogo, videos para redes, anuncios y ${palabraDeModeloActual().plural} virtuales.`,
};

/**
 * `/subscription` como pantalla propia — ADR-2026-07-31-copy-propio-default-comparativo.
 *
 * Es el único caso sin momento que lo dispare: el usuario entró a mirar planes, no
 * lo bloqueó nada ni acaba de generar su primera foto. Los otros copys pueden
 * apoyarse en lo que acaba de pasar; éste tiene que sostenerse solo, así que
 * vende el resultado —lo que la marca pasa a poder producir— y no el catálogo.
 *
 * Se usa además como copy **por defecto** del comparativo: en esa presentación el
 * carrusel no está, y era el que decía de qué se trata la pantalla.
 */
export const PRICING_COPY_SUSCRIPCION: PricingContextCopy = {
  title: 'Producí como una marca grande',
  description:
    `Fotos de catálogo, videos para redes, anuncios y ${palabraDeModeloActual().plural} virtuales: todo en minutos, sin estudio ni sesiones de fotos. Elegí el plan que acompaña el ritmo de tu marca.`,
};

/**
 * Por qué se abrió el catálogo, desde el punto de vista del saldo del usuario.
 * `mejorar` no es un estado de créditos sino una decisión: entró a mirar planes
 * teniendo con qué.
 */
export type MotivoPricing = 'sin' | 'pocos' | 'mejorar' | 'bloqueado';

/**
 * Copy y etiqueta de salida según el motivo — ADR-2026-07-31-copy-catalogo-sigue-saldo-real.
 *
 * Vive acá y no en cada pantalla porque son DOS los lugares que abren el catálogo
 * como overlay (el shell de `/inicio` y `SubscriptionLimitModal`, que a su vez lo
 * abre desde `/interfaz-v2` y otros 11 call-sites). Con la tabla duplicada, agregar
 * un motivo o retocar un texto arreglaba uno y dejaba el otro mintiendo — que es
 * justo lo que pasó: el catálogo decía "Te quedaste sin créditos" a alguien a quien
 * todavía le quedaban.
 *
 * `skipLabel` acompaña al copy: "Seguir sin créditos" no significa nada para quien
 * todavía tiene saldo.
 */
/**
 * Nota al pie del catálogo, debajo del botón de suscribirse. También cambia con el
 * contexto: "Al mejorar tu plan" no le habla a alguien que todavía no tiene uno
 * (ADR-2026-08-03-copy-prueba-gratis-suscribirse).
 */
export const PRICING_NOTA_DEFAULT =
  'Los créditos se renuevan cada mes. Al mejorar tu plan, el nuevo límite se aplica de inmediato.';

export const PRICING_NOTA_PRUEBA_GRATIS =
  'Los créditos se renuevan cada mes. Al suscribirte, quedan disponibles al instante.';

export interface CopyPricingOpts {
  /**
   * El usuario está en la **prueba gratis**, no en un plan pago. Cambia el copy de
   * "mejorá tu plan" por "suscribite": no tiene plan que mejorar.
   */
  pruebaGratis?: boolean;
}

export function copyPricingPorMotivo(
  motivo: MotivoPricing,
  opts: CopyPricingOpts = {},
): {
  copy: PricingContextCopy;
  skipLabel: string;
  nota: string;
} {
  const nota = opts.pruebaGratis ? PRICING_NOTA_PRUEBA_GRATIS : PRICING_NOTA_DEFAULT;
  if (motivo === 'sin') {
    return {
      copy: opts.pruebaGratis ? PRICING_COPY_SIN_CREDITOS_PRUEBA : PRICING_COPY_SIN_CREDITOS,
      skipLabel: 'Seguir sin créditos',
      nota,
    };
  }
  if (motivo === 'pocos') {
    return { copy: PRICING_COPY_POCOS_CREDITOS, skipLabel: 'Ahora no', nota };
  }
  // Tocó un modo que su plan no incluye (ADR-2026-08-03-modo-bloqueado-abre-pricing).
  // Reusa el copy del alta: es el mismo momento —le acaban de mostrar que hay más
  // Atlas del que puede usar— y ya está escrito para eso. No habla de créditos, que
  // es lo que lo separa de `sin` / `pocos`: acá el saldo no tiene nada que ver.
  //
  // Lleva `nota` como los otros tres: el tipo de retorno la exige desde que
  // `principal` la sumó a `mejorar`, y omitirla no compila. Es el detalle de saldo /
  // renovación, que acá no es el tema pero tampoco estorba.
  if (motivo === 'bloqueado') {
    return { copy: PRICING_COPY_ONBOARDING, skipLabel: 'Ahora no', nota };
  }
  return { copy: PRICING_COPY_MEJORAR_PLAN, skipLabel: 'Ahora no', nota };
}
